import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBarsProgress,
  faRectangleHistory,
  faFileCirclePlus,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons'; // or pro-regular, depending on your license

import {faEdit} from '@fortawesome/pro-regular-svg-icons';

interface RiskTag {
  label: string;
  level: 'Low' | 'Moderate' | 'High';
}

interface FieldOverviewProps {
  name: string;
  crop: string;
  areaHa: number;
  variety: string;
  planted: string;
  population: string;
  irrigation: string;
  yield: string;
  buyer: string;
  funder: string;
  risks: RiskTag[];
  onBack?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onViewDataLog?: () => void;
}

// Use your existing logic for background / text colors
const getRiskColor = (level: 'Low' | 'Moderate' | 'High') => {
  switch (level) {
    case 'Low':
      return '#E5F7F1';
    case 'Moderate':
      return '#FFF2E1';
    case 'High':
      return '#FDEAEA';
  }
};

const getRiskTextColor = (level: 'Low' | 'Moderate' | 'High') => {
  switch (level) {
    case 'Low':
      return '#259D64';
    case 'Moderate':
      return '#EF6820';
    case 'High':
      return '#972830';
  }
};

export const FieldOverview: React.FC<FieldOverviewProps> = ({
  name,
  crop,
  areaHa,
  variety,
  planted,
  population,
  irrigation,
  yield: expectedYield,
  buyer,
  funder,
  risks,
  onBack,
  onEdit,
  onDelete,
  onViewDataLog,
}) => {
  // Data items to display in the details grid
  const detailItems = [
    {label: 'Variety', value: variety},
    {label: 'Planted', value: planted},
    {label: 'Plant population', value: population},
    {label: 'Irrigation', value: irrigation},
    {label: 'Expected yield', value: expectedYield},
    {label: 'Buyer', value: buyer},
    {label: 'Funder', value: funder},
  ];

  return (
    <div className="field-overview-container">
      {/* Header row */}
      <div className="field-overview-header">
        <div className="left-header">
          <button className="back-button" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="header-text">
            <div className="field-name">{name}</div>
            <div className="field-subtitle">
              {crop} ({areaHa} ha)
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="edit-button" onClick={onEdit} title="Edit field">
            <FontAwesomeIcon icon={faEdit} />
          </button>
          {onDelete && (
            <button
              className="delete-button"
              onClick={onDelete}
              title="Delete field"
              aria-label="Delete field"
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          )}
        </div>
      </div>

      {/* Action Buttons - now each with an icon to the left */}
      <div className="overview-action-buttons">
        <div className="action-button clickable">
          <div className="icon-bg">
            <FontAwesomeIcon icon={faBarsProgress} className="action-icon" />
          </div>
          <div className="action-label">Create program</div>
        </div>

        <div className="action-button clickable" onClick={onViewDataLog}>
          <div className="icon-bg">
            <FontAwesomeIcon
              icon={faRectangleHistory}
              className="action-icon"
            />
          </div>
          <div className="action-label">View data log</div>
        </div>

        <div className="action-button clickable">
          <div className="icon-bg">
            <FontAwesomeIcon icon={faFileCirclePlus} className="action-icon" />
          </div>
          <div className="action-label">Generate report</div>
        </div>
      </div>

      <div className="overview-details-grid">
        {detailItems.map((item) => (
          <div className="detail-item" key={item.label}>
            <div className="detail-label">{item.label}</div>
            <div className="detail-value">{item.value}</div>
          </div>
        ))}
      </div>

      {/* Risk Tags */}
      <div className="risk-tags-container">
        {risks.map((risk, idx) => {
          const bgColor = getRiskColor(risk.level);
          const textColor = getRiskTextColor(risk.level);
          return (
            <div
              key={idx}
              className="risk-tag"
              style={{backgroundColor: bgColor}}
            >
              {/* The label is always #013333, so we let the SCSS define that */}
              <div className="risk-label">{risk.label}</div>

              {/* The risk level text uses the color from getRiskTextColor */}
              <div className="risk-level" style={{color: textColor}}>
                {risk.level} Risk
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
